import { Cache } from 'memory-cache';
import type MailboxInfo from 'owa-client-types/lib/MailboxInfo';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import {
    MIN_TIME_TO_EXPIRY_IN_MINUTES,
    TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS,
} from 'owa-tokenprovider-utils/lib/constants';
import type { IdentityInfo } from 'owa-auth-types';
import { getIdentityInfoKeyForRequest } from './getAnchorMailbox';
import addMinutes from 'owa-date-utc-fn/lib/addMinutes';
import type TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import addSeconds from 'owa-date-utc-fn/lib/addSeconds';

let tokenCache: UnifiedTokenCache;

/**
 * Gets the singleton instance of the UnifiedTokenCache.
 * Creates a new instance if one doesn't exist.
 *
 * @returns The singleton UnifiedTokenCache instance
 */
export function getUnifiedTokenCache(): UnifiedTokenCache {
    if (!tokenCache) {
        tokenCache = new UnifiedTokenCache();
    }
    return tokenCache;
}

export class UnifiedTokenCache {
    private tokenCache: Cache<string, TokenResponse>;

    /**
     * Initializes a new instance of UnifiedTokenCache with an empty cache.
     */
    public constructor() {
        this.tokenCache = new Cache();
    }

    /**
     * Retrieves a cached token if it exists and is valid for at least 1 minute.
     *
     * @param cacheKey - The unique key identifying the cached token
     * @returns The cached TokenResponse if valid, otherwise undefined
     */
    public getCachedToken(cacheKey: string): TokenResponse | undefined {
        let tokenResponse: TokenResponse | undefined = undefined;
        const cachedToken: TokenResponse = this.tokenCache.get(cacheKey) as TokenResponse;

        // Check that token is valid for at least 1 minute.
        if (cachedToken?.AccessTokenExpiry && cachedToken.AccessToken) {
            const expiryDateTimeWithMargin = addMinutes(
                new Date(cachedToken.AccessTokenExpiry),
                -MIN_TIME_TO_EXPIRY_IN_MINUTES
            );

            if (expiryDateTimeWithMargin > new Date(Date.now())) {
                tokenResponse = cachedToken;
            }
        }

        return tokenResponse;
    }

    /**
     * Stores a token in the cache with an automatic expiry time.
     * For enterprise tokens, adjusts the expiry time based on ExpiresIn or tokenExpiryDate.
     * Sets up a timeout callback to refresh the token before expiry.
     *
     * @param cacheKey - The unique key to store the token under
     * @param isEnterprise - Whether this is an enterprise (Office 365) token
     * @param tokenResponse - The token response object to cache
     * @param cacheTimeoutCallback - Optional callback to execute when the cache entry expires
     */
    public putCachedToken(
        cacheKey: string,
        isEnterprise: boolean,
        tokenResponse: TokenResponse,
        cacheTimeoutCallback?: () => Promise<TokenResponse | undefined>
    ): void {
        if (
            tokenResponse &&
            tokenResponse.TokenResultCode == 0 &&
            tokenResponse.AccessTokenExpiry
        ) {
            if (isEnterprise && tokenResponse.ExpiresIn && tokenResponse.ExpiresIn > 0) {
                const tokenExpiryDate = tokenResponse.AdditionalData?.get('tokenExpiryDate');
                if (tokenExpiryDate) {
                    tokenResponse.AccessTokenExpiry = tokenExpiryDate + 'Z'; //nh expiry date is in UTC
                } else {
                    tokenResponse.AccessTokenExpiry = addSeconds(
                        Date.now(),
                        tokenResponse.ExpiresIn
                    ).toISOString();
                }
            }

            const cacheTimeout: number =
                new Date(tokenResponse.AccessTokenExpiry).getTime() -
                Date.now() -
                TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS;

            if (cacheTimeout > 0) {
                this.tokenCache.put(cacheKey, tokenResponse, cacheTimeout, cacheTimeoutCallback);
            }
        }
    }

    /**
     * Generates a unique cache key by concatenating resource, identity, scope, app ID, and tenant ID.
     *
     * @param identitySource - Optional mailbox or identity information
     * @param resource - Optional resource identifier
     * @param scope - Optional OAuth scope
     * @param childAppId - Optional child application ID
     * @param targetTenantId - Optional target tenant ID
     * @returns A pipe-delimited string combining all provided parameters
     */
    public getCacheKey(
        identitySource: MailboxInfo | IdentityInfo,
        resource?: string,
        scope?: string,
        targetTenantId?: string,
        childAppId?: string
    ): string {
        const identityInfoKey = getIdentityInfoKeyForRequest(identitySource);
        let key: string = resource ?? '';
        key = key + '|' + identityInfoKey;

        if (scope) {
            key = key + '|' + scope;
        }

        if (childAppId) {
            key = key + '|' + childAppId;
        }

        if (targetTenantId) {
            key = key + '|' + targetTenantId;
        }

        return key;
    }

    /**
     * Deletes a token from the cache using its cache key.
     *
     * @param cacheKey - The unique key identifying the token to delete
     */
    public deleteCachedToken(cacheKey: string): void {
        this.tokenCache.del(cacheKey);
    }

    /**
     * Returns all cache keys currently stored in the token cache.
     *
     * @returns An array of all cache key strings
     */
    public getAllCacheKeys(): string[] {
        return this.tokenCache.keys();
    }

    /**
     * Clears all cached tokens associated with a specific identity.
     * Iterates through all cache keys and deletes those containing the identity key.
     *
     * @param identitySource - The mailbox or identity information to clear tokens for
     */
    public clearCacheForIdentity(identitySource?: MailboxInfo | IdentityInfo): void {
        if (identitySource) {
            const identityInfoKey = getIdentityInfoKeyForRequest(identitySource);

            const cacheKeys = this.getAllCacheKeys();
            for (const key of cacheKeys) {
                if (key.includes(identityInfoKey)) {
                    this.deleteCachedToken(key);
                }
            }
        }
    }
}
